import { createSelector } from '@ngrx/store';


import { Charge, FullJobFragment, Price, PriceType } from 'graphql.generated';

import { isNil } from 'lodash';

import { eventTypeInfoMapV2 } from 'src/app/global.constants';
import { isFinalizedInvoice } from 'src/app/invoices/invoices.utils';
import { ChargeWithKey } from 'src/app/job/job-workorders/job-workorders.interfaces';

import { zonesFeature } from '../../../app/core/zones/zones.reducer';
import { estimateBlockedReason, isBillableEvent } from '../../shared/create-document-dialog/create-document-dialog.util';
import { brandingFeature, createCheckSinglePermissionSelector, createConfigSelector } from '../../state/branding.store';
import { getZoneActivePrice } from '../../utilities/product.util';
import { generateUUID } from '../../utilities/state.util';
import { getProductsWithClosestPricesByJobZone } from '../job-charges-helpers';
import { CalendarEventWithLockedAndInvoicedFlag, ChargeWithLockedAndInvoicedFlag } from '../job-create/job-interfaces';
import { getDamagesFromCharges, isLockExemptNonRevenueEvent } from '../job-helpers';
import { selectJob, selectJobZone } from '../job-tool.reducer';

import { jobWorkordersFeature } from './job-workorders.feature';
import { EditableCharge } from './job-workorders.state';


export const selectCanModifyLockedEvents = createCheckSinglePermissionSelector({
  permission: 'calendarEvents.edit',
  restriction: {
    modifyLockedEvents: true,
  },
});

/**
 * Requirement: [7004] - the navigator is for Crew Lead and above. Starting it writes through
 * `updateEventNavigator`, which guards on `calendarEvents.edit`; Crew Members hold only
 * `calendarEvents.view`, so without this they see a Start Navigator button whose first write
 * the server rejects. Visibility only — the server check is the enforcement.
 */
export const selectCanRunNavigator = createCheckSinglePermissionSelector({
  permission: 'calendarEvents.edit',
});

export const selectLockDate =createConfigSelector('ROLLING__LOCK__DATE_CURRENTDATE');

const checkLockedAndInvoiced = (calendarEvent: FullJobFragment['events'][number], canModifyLockedEvents: boolean, lockDate: number) => {

  const isLockDatePassed = calendarEvent?.end && (lockDate > calendarEvent?.end);
  const locked = Boolean(!canModifyLockedEvents && isLockDatePassed && !isLockExemptNonRevenueEvent(calendarEvent));

  const invoiced = Boolean(calendarEvent?.invoices?.some(isFinalizedInvoice));

  return { locked, invoiced };
};

const checkChargeLockedAndInvoiced = (
  calendarEvent: FullJobFragment['events'][number],
  canModifyLockedEvents: boolean,
  lockDate: number,
) => {

  const isLockDatePassed = calendarEvent?.end && (lockDate > calendarEvent?.end);
  const locked = Boolean(!canModifyLockedEvents && isLockDatePassed);
  const invoiced = Boolean(calendarEvent?.invoices?.some(isFinalizedInvoice));

  return { locked, invoiced };
};

export const selectJobEventsWithLockedAndInvoicedFlag = createSelector(
  selectJob,
  selectCanModifyLockedEvents,
  selectLockDate,
  (job, canModifyLockedEvents, lockDate) => {

    const updatedEvents = job?.events?.map(event => {

      const { locked, invoiced } = checkLockedAndInvoiced(event, canModifyLockedEvents, lockDate);

      return {
        ...event,
        locked,
        invoiced
      } as CalendarEventWithLockedAndInvoicedFlag;
    });

    return updatedEvents || [];
});

export const selectChargesWithLockedAndInvoicedFlag = createSelector(
  selectJob,
  selectCanModifyLockedEvents,
  selectLockDate,
  (job, canModifyLockedEvents, lockDate) => {
    const updatedCharges = job?.charges?.map(charge => {

      const { locked, invoiced } = checkChargeLockedAndInvoiced(charge?.calendarEvent, canModifyLockedEvents, lockDate);

      return {
        ...charge,
        calendarEvent: {
          ...charge?.calendarEvent,
          locked,
          invoiced
        }
      } as ChargeWithLockedAndInvoicedFlag;
    });

    return updatedCharges || [];
  }
);

export const selectDamages = createSelector(
  selectChargesWithLockedAndInvoicedFlag,
  (charges) => {
    return getDamagesFromCharges(charges);
  },
);

//apply changes from chargesUpdates

//each charge has calendarEvent field. When we move charges between events, we don't update that calendarEvent,
//only move charge in events array
export const selectProductsSuggestionsForCustomCharges = createSelector(
  selectJob,
  brandingFeature.selectProducts,
  (job, products) => {
    return getProductsWithClosestPricesByJobZone(products, job?.zone);
  }
);

export const selectActivePriceForZone = (prices: Partial<Price>[], zoneId?: string) =>
  createSelector(
    zonesFeature.selectCurrentAppZone,
    (currentAppZone) => {
      let zonePrice;
      let effectiveZoneId = zoneId;

      // Filter out deleted prices
      const existingPrices = prices?.filter((p) => !p.deletedAt) || [];
      // Filter out inactive prices
      const activePrices = existingPrices.filter((p) => p.active);

      // If only one active price exists, return it
      if (activePrices.length === 1) {
        return activePrices[0];
      }

      // Find exact zone price if it exists
      if (effectiveZoneId) {
        zonePrice = activePrices.find((ap) => ap.zone?.id === effectiveZoneId);
      }

      if (!effectiveZoneId || !zonePrice) {
        // Default to contexted zone
        effectiveZoneId = currentAppZone?.id;
        // Check for price in contexted zone
        zonePrice = activePrices.find((ap) => ap.zone?.id === effectiveZoneId);
      }

      // If we have an exact zone match then return it
      if (zonePrice) {
        return zonePrice;
      }

      return activePrices[0];
    }
  );

export const selectPriceType = (charge: ChargeWithKey) =>
  createSelector(
    selectActivePriceForZone(charge.product?.prices || []),
    (activePrice) => {
      if (charge.price) {
        return charge.price.priceType;
      };

      if (charge.product && activePrice) {
        return activePrice.priceType;
      };

      return 'fixed';
    }
  );

export const selectUneditedAmount = (charge: ChargeWithKey) =>
  createSelector(
    // Pass product prices if available, otherwise an empty array
    selectActivePriceForZone(charge?.product?.prices || []),
    (activePrice) => {
      if (charge?.amount !== null && charge?.amount !== undefined) {
        return charge?.amount;
      }

      if (charge?.price) {
        return charge?.price?.amount;
      }

      if (charge?.product && activePrice) {
        return activePrice?.amount || 0;
      }

      return 0;
    }
  );

export const selectEditableAmounts = (events: { charges: Partial<Charge>[] }[]) =>
  createSelector(
    (state) => state,
    (state) => {
      const editableAmounts = {};

      events?.forEach(event => {
        event.charges?.forEach((charge) => {
          const amount = selectUneditedAmount(charge)(state as object);
          const priceType = selectPriceType(charge)(state as object);

          editableAmounts[charge.id] = priceType === 'fixed' ? amount / 100 : amount;
        });
      });

      return editableAmounts;
    }
  );

export const selectEventIds = createSelector(
  selectJobEventsWithLockedAndInvoicedFlag,
  (events) => {
    const validEventIds: string[] = [];
    const existingEventIds = events.map((e) => e.id);


    for (const event of events) {


      if (!event.locked
        && !event?.invoices?.some(isFinalizedInvoice)
        && event?.type !== eventTypeInfoMapV2?.estimating?.value
        && event?.type !== eventTypeInfoMapV2?.virtualEstimate?.value
      ) {
        validEventIds.push(event.id);
      }
    }


    return {
      validEventIds,
      existingEventIds
    };
  }
);

// event doesn't have any unsaved changes from workorders
export const selectEventById = (eventId: string) =>
  createSelector(selectJob, (job) =>
    job?.events?.find(
      (e): e is CalendarEventWithLockedAndInvoicedFlag => e?.id === eventId
    )
  );

export const selectEventInvoice = (eventId: string) =>
  createSelector(selectJob, (job) => {
    const event = job?.events?.find(
      (e): e is CalendarEventWithLockedAndInvoicedFlag => e?.id === eventId
    );
    const invoice = event?.invoices?.find(i => isNil(i.voidedAt) && isNil(i.deletedAt));
    return invoice;
  }
  );

export const selectAddChargesDialogPresetCharges = createSelector(
  jobWorkordersFeature.selectAddChargesDialog,
  brandingFeature.selectProducts,
  brandingFeature.selectBranding,
  selectJobZone,
  ({ selectedProducts }, availableProducts, branding, jobZone): EditableCharge[] => {
    const zoneId = branding?.zoneId;
    return Object.keys(selectedProducts)
      .map((productId): EditableCharge | null => {
        const product = availableProducts?.find((p) => p.id === productId);
        if (!product) { return null; }
        const quantity = selectedProducts[productId];
        const activePrice = getZoneActivePrice(product.prices, zoneId);
        if (!activePrice) { return null; }
        const amount = activePrice.priceType === PriceType.Percentage ? activePrice.amount : activePrice.amount / 100;
        const taxes = (activePrice?.taxes || [])
          .filter((t) => t.zones?.nodes?.some((z) => z?.id === zoneId || z?.id === jobZone?.id))
          .map((t) => ({ id: t.id, percentage: t.percentage }));

        return {
          quantity,
          amount,
          priceType: activePrice?.priceType,
          productId,
          chargeId: generateUUID(),
          new: true,
          modified: false,
          removed: false,
          expenses: [],
          taxes,
          attributes: (activePrice?.attributes || []).filter(Boolean),
        };
      })
      .filter((charge): charge is EditableCharge => charge !== null && charge.quantity > 0);
  },
);

export const selectAddEstimateBlockedReason = createSelector(
  jobWorkordersFeature.selectWorkordersEditor,
  ({ workorders }) => estimateBlockedReason(
    workorders
      .filter((wo) => !wo.removed)
      .some((wo) => isBillableEvent({
        status: wo.cancelled ? 'cancelled' : undefined,
        charges: wo.charges.filter((charge) => !charge.removed),
      })),
  ),
);

export const workOrdersSelectors = {
  selectEventIds,
  selectEditableAmounts,
  selectJobEventsWithLockedAndInvoicedFlag,
  selectEventById,
  selectEventInvoice,
  selectProductsSuggestionsForCustomCharges,
};

/** Number of Event Notes 📎 uploads currently running across workorders.
 *  Shared (job.effects waits on it before/inside the save gate), so it lives
 *  in the selectors file composed from the FEATURE selector — a hard-coded
 *  root-state key index would evaluate to 0 uploads (silently unblocking the
 *  save) if the feature ever renames; the feature composition becomes a
 *  compile error instead (CodeRabbit). */
export const selectEventUploadsInFlight = createSelector(
  jobWorkordersFeature.selectWorkordersEditor,
  ({ eventUploads }) => Object.values(eventUploads ?? {}).reduce((sum, n) => sum + n, 0),
);
